import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { CloudService } from 'src/app/services/cloud.service';

interface CaseStudyCard {
  id: string;
  title: string;
  tag: string;
  clientName: string;
  overview: string;
}

@Component({
  selector: 'app-case-studies',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './case-studies.component.html',
  styleUrls: ['./case-studies.component.css']
})
export class CaseStudiesComponent implements OnInit, OnDestroy {

  caseStudies: CaseStudyCard[] = [];
  loading = false;
  page = 1;
  limit = 20;
  totalCount = 0;
  private fetchSub?: Subscription;

  constructor(
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly router: Router,
    private readonly cloudService: CloudService,
    private readonly notify: NotificationService
  ) { }

  ngOnInit(): void {
    this.loadCaseStudies();
  }

  ngOnDestroy(): void {
    this.fetchSub?.unsubscribe();
  }

  loadCaseStudies(): void {
    this.loading = true;
    this.fetchSub?.unsubscribe();

    this.fetchSub = this.cloudService.getAllCaseStudies(this.page, this.limit).subscribe({
      next: (data: any) => {
        const response = data?.object;
        const stories = response?.DATA?.stories;
        const source = Array.isArray(stories) ? stories : [];

        this.caseStudies = source.map((story: any, index: number) => this.mapCaseStudy(story, index));

        const total = Number(response?.TOTAL_COUNT);
        const page = Number(response?.PAGE);
        const limit = Number(response?.LIMIT);

        this.totalCount = Number.isFinite(total) && total >= 0 ? total : this.caseStudies.length;
        this.page = Number.isFinite(page) && page > 0 ? page : this.page;
        this.limit = Number.isFinite(limit) && limit > 0 ? limit : this.limit;
        this.loading = false;
      },
      error: (e) => {
        this.caseStudies = [];
        this.totalCount = 0;
        this.loading = false;
        this.notify.showNotification(
          e?.error?.message,
          'top',
          'error',
          e?.error?.status
        );
      }
    });
  }

  get totalPages(): number {
    if (!this.totalCount || !this.limit) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.limit));
  }

  get startItem(): number {
    if (!this.totalCount) {
      return 0;
    }
    return (this.page - 1) * this.limit + 1;
  }

  get endItem(): number {
    if (!this.totalCount) {
      return 0;
    }
    return Math.min(this.page * this.limit, this.totalCount);
  }

  get paginationItems(): Array<number | '...'> {
    const total = this.totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: Array<number | '...'> = [1];
    let start = Math.max(2, this.page - 1);
    let end = Math.min(total - 1, this.page + 1);

    if (this.page <= 3) {
      start = 2;
      end = 4;
    } else if (this.page >= total - 2) {
      start = total - 3;
      end = total - 1;
    }

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total - 1) {
      pages.push('...');
    }

    pages.push(total);
    return pages;
  }

  goToPage(targetPage: number): void {
    if (targetPage < 1 || targetPage > this.totalPages || targetPage === this.page) {
      return;
    }

    this.page = targetPage;
    this.loadCaseStudies();
    this.scrollToCaseStudiesTop();
  }

  goToPreviousPage(): void {
    if (this.page > 1) {
      this.goToPage(this.page - 1);
    }
  }

  goToNextPage(): void {
    if (this.page < this.totalPages) {
      this.goToPage(this.page + 1);
    }
  }

  openCaseStudy(caseStudyId: string): void {
    this.router.navigate(['/case-study', caseStudyId]);
    this.scrollToTop();
  }

  private mapCaseStudy(story: any, index: number): CaseStudyCard {
    const content = story?.content ?? {};
    const title = content?.heading || story?.name || 'Untitled Case Study';
    const clientName = content?.client || 'Enterprise Client';
    const tag = content?.category || 'Case Study';
    const overview = content?.short_description || 'No overview available.';
    const idValue = story?.slug || story?.uuid || story?.id || index + 1;

    return {
      id: String(idValue),
      title,
      tag,
      clientName,
      overview
    };
  }

  private scrollToCaseStudiesTop(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const grid = this.doc.querySelector('.case-studies-grid, .case-studies-loading, .case-studies-empty');
    if (!grid) {
      return;
    }

    const y = grid.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  private scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

}
